import { Link, useLocation } from 'react-router-dom';
import { House, Plus, PawPrint, Bell, Compass, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsProvider } from '@/hooks/useIsProvider';
import NotificationBadge from '@/components/NotificationBadge';
import CreateActionSheet from '@/components/CreateActionSheet';
import { cn } from '@/lib/utils';

const BottomTabNavigation = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const { data: isProvider } = useIsProvider();

  const isActive = (path: string) => {
    if (path === '/feed') return location.pathname === '/feed';
    if (path === '/my-pets') {
      return (
        location.pathname === '/my-pets' ||
        location.pathname.startsWith('/pet/') ||
        location.pathname.startsWith('/edit-pet/')
      );
    }
    if (path === '/notifications') return location.pathname === '/notifications';
    if (path === '/explore') return location.pathname === '/explore';
    return location.pathname === path;
  };

  const tabStyle =
    'flex flex-col items-center justify-center h-16 flex-1 transition-colors min-w-0';
  const activeStyle = 'text-pet-purple';
  const inactiveStyle = 'text-gray-500';

  if (!user) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 safe-area-bottom">
        <div className="flex relative">
          {/* Feed */}
          <Link
            to="/feed"
            className={`${tabStyle} ${isActive('/feed') ? activeStyle : inactiveStyle}`}
          >
            <House className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">{t('navigation.feed')}</span>
          </Link>

          {/* My Pets / Provider Dashboard */}
          {isProvider ? (
            <Link
              to="/provider-dashboard"
              className={`${tabStyle} ${
                location.pathname === '/provider-dashboard' ? activeStyle : inactiveStyle
              }`}
            >
              <LayoutDashboard className="h-6 w-6 mb-1" />
              <span className="text-xs font-medium">Dashboard</span>
            </Link>
          ) : (
            <Link
              to="/my-pets"
              className={`${tabStyle} ${isActive('/my-pets') ? activeStyle : inactiveStyle}`}
            >
              <PawPrint className="h-6 w-6 mb-1" />
              <span className="text-xs font-medium">{t('navigation.myPets')}</span>
            </Link>
          )}

          {/* Create — centro elevado, idéntico iOS/Android */}
          <div className="flex-1 flex flex-col items-center justify-end relative">
            {isProvider ? (
              <div className="opacity-40 cursor-not-allowed flex flex-col items-center -translate-y-3">
                <div className="bg-gray-300 rounded-full h-14 w-14 flex items-center justify-center shadow-md">
                  <Plus className="h-6 w-6 text-gray-500" />
                </div>
                <span className="text-[10px] font-medium text-gray-500 mt-1">N/A</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowCreateSheet(true)}
                aria-label={t('navigation.create')}
                className={cn(
                  'flex flex-col items-center justify-center -translate-y-4 focus:outline-none',
                  'transition-transform active:scale-95'
                )}
              >
                <span
                  className={cn(
                    'flex h-14 w-14 items-center justify-center rounded-full',
                    'bg-gradient-to-br from-pet-purple to-pet-blue text-white',
                    'shadow-xl ring-4 ring-white'
                  )}
                >
                  <Plus className="h-7 w-7" />
                </span>
                <span className="text-[10px] font-semibold text-pet-purple mt-1">
                  {t('navigation.create')}
                </span>
              </button>
            )}
          </div>

          {/* Alerts */}
          <Link
            to="/notifications"
            className={`${tabStyle} ${
              isActive('/notifications') ? activeStyle : inactiveStyle
            }`}
          >
            <div className="relative">
              <Bell className="h-6 w-6 mb-1" />
              <NotificationBadge />
            </div>
            <span className="text-xs font-medium">{t('navigation.alerts')}</span>
          </Link>

          {/* Explore (replaces Categories) */}
          <Link
            to="/explore"
            className={`${tabStyle} ${isActive('/explore') ? activeStyle : inactiveStyle}`}
          >
            <Compass className="h-6 w-6 mb-1" />
            <span className="text-xs font-medium">
              {t('navigation.explore', 'Explore')}
            </span>
          </Link>
        </div>
      </div>

      <CreateActionSheet
        isOpen={showCreateSheet}
        onClose={() => setShowCreateSheet(false)}
      />
    </>
  );
};

export default BottomTabNavigation;
